/**
 * Cola de envío de ensayos en memoria (nunca en localStorage/sessionStorage).
 * - Envía en serie y sin bloquear la presentación: `enqueue` devuelve de inmediato.
 * - Reintentos limitados con espera exponencial para errores de red / 5xx / 408 / 429.
 * - Idempotente: cada ensayo lleva su `clientTrialId`; reenviar el mismo no duplica en servidor.
 * - Si se agotan los reintentos, el ensayo queda en `failed` y se notifica `onFatal`.
 */
import type { TrialCreate } from "@gazelab/shared";
import { ApiError } from "../../services/api.js";

export interface SaveQueueOptions {
  send: (trial: TrialCreate) => Promise<unknown>;
  maxAttempts?: number;
  baseDelayMs?: number;
  onFatal?: (trial: TrialCreate, error: unknown) => void;
  /** Error no recuperable (4xx de validación): el ensayo se descarta del envío y se informa. */
  onRejected?: (trial: TrialCreate, error: unknown) => void;
  sleep?: (ms: number) => Promise<void>;
}

export function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 || error.status === 408 || error.status === 429;
  // Errores de fetch (TypeError: Failed to fetch) u otros fallos de red.
  return true;
}

export class TrialSaveQueue {
  private pending: TrialCreate[] = [];
  private readonly failedTrials: TrialCreate[] = [];
  private readonly sentIds = new Set<string>();
  private running: Promise<void> | null = null;
  private readonly maxAttempts: number;
  private readonly baseDelayMs: number;
  private readonly sleep: (ms: number) => Promise<void>;

  constructor(private readonly opts: SaveQueueOptions) {
    this.maxAttempts = opts.maxAttempts ?? 5;
    this.baseDelayMs = opts.baseDelayMs ?? 500;
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  }

  get size(): number {
    return this.pending.length;
  }

  get failed(): ReadonlyArray<TrialCreate> {
    return this.failedTrials;
  }

  get sentCount(): number {
    return this.sentIds.size;
  }

  enqueue(trial: TrialCreate): void {
    if (this.sentIds.has(trial.clientTrialId) || this.pending.some((t) => t.clientTrialId === trial.clientTrialId)) return;
    this.pending.push(trial);
    this.kick();
  }

  /** Vuelve a encolar los ensayos que fallaron (p. ej. al reanudar o antes de finalizar). */
  retryFailed(): void {
    const items = this.failedTrials.splice(0);
    for (const t of items) this.enqueue(t);
  }

  /** Espera a que la cola quede vacía. Resuelve `true` si no quedaron ensayos fallidos. */
  async flush(): Promise<boolean> {
    while (this.running) await this.running;
    return this.failedTrials.length === 0;
  }

  /** Descarta todo lo pendiente (p. ej. al cancelar la sesión). */
  clear(): void {
    this.pending = [];
    this.failedTrials.splice(0);
  }

  private kick() {
    if (this.running) return;
    this.running = this.drain().finally(() => {
      this.running = null;
      if (this.pending.length > 0) this.kick();
    });
  }

  private async drain() {
    while (this.pending.length > 0) {
      const trial = this.pending[0]!;
      let attempt = 0;
      for (;;) {
        attempt++;
        try {
          await this.opts.send(trial);
          this.sentIds.add(trial.clientTrialId);
          break;
        } catch (e) {
          if (!isRetryable(e)) {
            this.opts.onRejected?.(trial, e);
            break;
          }
          if (attempt >= this.maxAttempts) {
            this.failedTrials.push(trial);
            this.opts.onFatal?.(trial, e);
            break;
          }
          await this.sleep(this.baseDelayMs * 2 ** (attempt - 1));
        }
      }
      this.pending.shift();
    }
  }
}
